/**
 * DemocratCharts — indicator math.
 *
 * Pure calculation functions. All return arrays aligned 1:1 with the input
 * candles array (with leading nulls during the warmup period).
 *
 * RSI and MACD are included for completeness even though their UI lives in
 * a separate pane (Phase 5.1). For now only EMA/SMA/VWAP/BB are wired up.
 */

import type { Candle } from "@/hooks/useCandles";

export interface IndicatorPoint {
  ts: string;
  value: number | null;
}

/** Exponential Moving Average. */
export function calculateEMA(candles: Candle[], period: number): IndicatorPoint[] {
  if (period <= 1 || candles.length === 0) {
    return candles.map((c) => ({ ts: c.ts, value: c.close }));
  }
  const k = 2 / (period + 1);
  const out: IndicatorPoint[] = [];
  let ema: number | null = null;
  let sum = 0;

  for (let i = 0; i < candles.length; i++) {
    const c = candles[i];
    if (i < period) {
      sum += c.close;
      if (i === period - 1) {
        ema = sum / period;
        out.push({ ts: c.ts, value: ema });
      } else {
        out.push({ ts: c.ts, value: null });
      }
    } else {
      ema = c.close * k + (ema as number) * (1 - k);
      out.push({ ts: c.ts, value: ema });
    }
  }
  return out;
}

/** Simple Moving Average. */
export function calculateSMA(candles: Candle[], period: number): IndicatorPoint[] {
  if (period <= 1 || candles.length === 0) {
    return candles.map((c) => ({ ts: c.ts, value: c.close }));
  }
  const out: IndicatorPoint[] = [];
  for (let i = 0; i < candles.length; i++) {
    if (i < period - 1) {
      out.push({ ts: candles[i].ts, value: null });
    } else {
      let sum = 0;
      for (let j = i - period + 1; j <= i; j++) sum += candles[j].close;
      out.push({ ts: candles[i].ts, value: sum / period });
    }
  }
  return out;
}

/**
 * VWAP — Volume Weighted Average Price.
 *
 * Conventional VWAP resets each session (trading day). For chart use across
 * many sessions, traders typically expect a *rolling* anchor — but the common
 * default is per-day. This implementation resets at midnight UTC of each day,
 * which works correctly for daily-and-finer timeframes.
 */
export function calculateVWAP(candles: Candle[]): IndicatorPoint[] {
  const out: IndicatorPoint[] = [];
  let lastDay = "";
  let cumPV = 0;
  let cumV = 0;

  for (const c of candles) {
    const day = c.ts.slice(0, 10); // YYYY-MM-DD
    if (day !== lastDay) {
      cumPV = 0;
      cumV = 0;
      lastDay = day;
    }
    const typical = (c.high + c.low + c.close) / 3;
    cumPV += typical * c.volume;
    cumV += c.volume;
    out.push({ ts: c.ts, value: cumV > 0 ? cumPV / cumV : null });
  }
  return out;
}

/** Bollinger Bands — upper/middle/lower around SMA(period) with stdDev × multiplier. */
export interface BollingerBands {
  upper: IndicatorPoint[];
  middle: IndicatorPoint[];
  lower: IndicatorPoint[];
}

export function calculateBollingerBands(
  candles: Candle[],
  period = 20,
  multiplier = 2
): BollingerBands {
  const upper: IndicatorPoint[] = [];
  const middle: IndicatorPoint[] = [];
  const lower: IndicatorPoint[] = [];

  for (let i = 0; i < candles.length; i++) {
    if (i < period - 1) {
      upper.push({ ts: candles[i].ts, value: null });
      middle.push({ ts: candles[i].ts, value: null });
      lower.push({ ts: candles[i].ts, value: null });
      continue;
    }
    let sum = 0;
    for (let j = i - period + 1; j <= i; j++) sum += candles[j].close;
    const mean = sum / period;

    let sqSum = 0;
    for (let j = i - period + 1; j <= i; j++) {
      sqSum += (candles[j].close - mean) ** 2;
    }
    const std = Math.sqrt(sqSum / period);

    upper.push({  ts: candles[i].ts, value: mean + multiplier * std });
    middle.push({ ts: candles[i].ts, value: mean });
    lower.push({  ts: candles[i].ts, value: mean - multiplier * std });
  }
  return { upper, middle, lower };
}

/** RSI — Wilder's smoothed average. Output in 0..100 range. */
export function calculateRSI(candles: Candle[], period = 14): IndicatorPoint[] {
  const out: IndicatorPoint[] = [];
  if (candles.length === 0) return out;

  let avgGain = 0;
  let avgLoss = 0;

  for (let i = 0; i < candles.length; i++) {
    if (i === 0) {
      out.push({ ts: candles[i].ts, value: null });
      continue;
    }
    const change = candles[i].close - candles[i - 1].close;
    const gain = Math.max(0, change);
    const loss = Math.max(0, -change);

    if (i <= period) {
      avgGain = (avgGain * (i - 1) + gain) / i;
      avgLoss = (avgLoss * (i - 1) + loss) / i;
      if (i < period) {
        out.push({ ts: candles[i].ts, value: null });
        continue;
      }
    } else {
      avgGain = (avgGain * (period - 1) + gain) / period;
      avgLoss = (avgLoss * (period - 1) + loss) / period;
    }

    if (avgLoss === 0) {
      out.push({ ts: candles[i].ts, value: 100 });
    } else {
      const rs = avgGain / avgLoss;
      out.push({ ts: candles[i].ts, value: 100 - 100 / (1 + rs) });
    }
  }
  return out;
}

/**
 * MFI — Money Flow Index.
 *
 * Like RSI but weighted by volume — uses typical price (H+L+C)/3 times
 * volume to measure buying vs selling pressure. Range 0..100.
 *
 * Overbought > 80, oversold < 20 by convention.
 */
export function calculateMFI(candles: Candle[], period = 14): IndicatorPoint[] {
  const out: IndicatorPoint[] = [];
  if (candles.length === 0) return out;

  const typical = candles.map((c) => (c.high + c.low + c.close) / 3);
  const rawMF = candles.map((c, i) => typical[i] * c.volume);

  for (let i = 0; i < candles.length; i++) {
    if (i < period) {
      out.push({ ts: candles[i].ts, value: null });
      continue;
    }
    let posMF = 0;
    let negMF = 0;
    for (let j = i - period + 1; j <= i; j++) {
      if (j === 0) continue;
      if (typical[j] > typical[j - 1]) posMF += rawMF[j];
      else if (typical[j] < typical[j - 1]) negMF += rawMF[j];
      // equal → neither side
    }
    if (negMF === 0) {
      out.push({ ts: candles[i].ts, value: 100 });
    } else {
      const mr = posMF / negMF;
      out.push({ ts: candles[i].ts, value: 100 - 100 / (1 + mr) });
    }
  }
  return out;
}

/**
 * Moon phase — fraction of synodic month elapsed since a known new moon.
 *
 * Returns 0..1 where:
 *   0.00 = New Moon         🌑
 *   0.25 = First Quarter    🌓
 *   0.50 = Full Moon        🌕
 *   0.75 = Last Quarter     🌗
 *
 * Synodic month = 29.530588853 days (mean lunar cycle).
 * Reference new moon: 2000-01-06 18:14 UTC.
 *
 * Accuracy: good to ~±1 day, plenty for charting purposes.
 */
const SYNODIC_DAYS = 29.530588853;
const REFERENCE_NEW_MOON_MS = Date.UTC(2000, 0, 6, 18, 14);

export function moonPhase(date: Date): number {
  const elapsedDays = (date.getTime() - REFERENCE_NEW_MOON_MS) / (24 * 60 * 60 * 1000);
  const cycles = elapsedDays / SYNODIC_DAYS;
  const frac = cycles - Math.floor(cycles);
  return frac < 0 ? frac + 1 : frac;
}

export type MoonEventKind = "new" | "first_quarter" | "full" | "last_quarter";

export interface MoonEvent {
  ts: string;
  kind: MoonEventKind;
}

/**
 * Find moon-phase events near each candle — emits a marker whenever the
 * phase crosses a quarter boundary (0, 0.25, 0.5, 0.75).
 */
export function calculateMoonEvents(candles: Candle[]): MoonEvent[] {
  const events: MoonEvent[] = [];
  if (candles.length < 2) return events;

  const quarters: [number, MoonEventKind][] = [
    [0,    "new"],
    [0.25, "first_quarter"],
    [0.5,  "full"],
    [0.75, "last_quarter"],
  ];

  for (let i = 1; i < candles.length; i++) {
    const prev = moonPhase(new Date(candles[i - 1].ts));
    const cur  = moonPhase(new Date(candles[i].ts));

    for (const [q, kind] of quarters) {
      // Detect a crossing of quarter q between prev and cur.
      // Handle the wrap from ~0.99 → 0.01 for the new moon (q=0).
      if (q === 0) {
        if (prev > 0.75 && cur < 0.25) {
          events.push({ ts: candles[i].ts, kind });
        }
      } else if (prev < q && cur >= q) {
        events.push({ ts: candles[i].ts, kind });
      }
    }
  }
  return events;
}

/** MACD — fast EMA, slow EMA, signal line, histogram. */
export interface MACD {
  macd: IndicatorPoint[];
  signal: IndicatorPoint[];
  histogram: IndicatorPoint[];
}

export function calculateMACD(
  candles: Candle[],
  fastPeriod = 12,
  slowPeriod = 26,
  signalPeriod = 9
): MACD {
  const fast = calculateEMA(candles, fastPeriod);
  const slow = calculateEMA(candles, slowPeriod);

  const macd: IndicatorPoint[] = candles.map((c, i) => ({
    ts: c.ts,
    value:
      fast[i].value !== null && slow[i].value !== null
        ? (fast[i].value as number) - (slow[i].value as number)
        : null,
  }));

  // EMA of the macd line — computed ONLY over the non-null region. Feeding
  // the warmup nulls in as zeros contaminates the EMA's SMA seed, making the
  // signal line (and histogram) wrong for ~slow+signal bars and biased after.
  const firstIdx = macd.findIndex((m) => m.value !== null);
  const signal: IndicatorPoint[] = macd.map((m) => ({ ts: m.ts, value: null }));
  if (firstIdx >= 0) {
    const validAsCandles = macd.slice(firstIdx).map((m) => ({
      ts: m.ts, open: 0, high: 0, low: 0, close: m.value as number, volume: 0,
    })) as Candle[];
    const sig = calculateEMA(validAsCandles, signalPeriod);
    sig.forEach((p, j) => { signal[firstIdx + j] = p; });
  }

  const histogram: IndicatorPoint[] = candles.map((c, i) => ({
    ts: c.ts,
    value:
      macd[i].value !== null && signal[i].value !== null
        ? (macd[i].value as number) - (signal[i].value as number)
        : null,
  }));

  return { macd, signal, histogram };
}
